import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	NgZone,
	OnInit,
	Renderer2,
	signal,
	Signal,
} from '@angular/core';
import { TransportAction, TransportModule, TransportSide } from '@/new-transport/_models/transport.model';
import { TopBarComponent } from '@/new-transport/_shared/topbar/top-bar.component';
import { TransportTimetableStore } from '@/new-transport/transport-timetable/_store/transport-timetable.store';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatCardModule } from '@angular/material/card';
import {
	StopConnection,
	StopKindColor,
	TimetableCity,
	TimetableWithNestedAntennas,
	TransportLogic,
	TransportTimetable,
	TransportVehicleKind,
	VehicleInGroupedTimetable,
} from '@/new-transport/_models/transport-timetable.model';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { KeyValuePipe, NgClass, NgStyle } from '@angular/common';
import { TransportTimetableTableComponent } from '@/new-transport/transport-timetable/transport-timetable-table/transport-timetable-table.component';
import { CustomMatLoaderComponent } from '@/common/custom-mat-loader/custom-mat-loader.component';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, map, pipe, tap } from 'rxjs';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { ActivatedRoute } from '@angular/router';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { TransportLuggageComponent } from './transport-luggage/transport-luggage.component';

export interface ExpansionPanelOpenState {
	expanded: boolean;
	antennas: ExpansionPanelOpenState[];
}

@Component({
	selector: 'app-transport-timetable',
	standalone: true,
	imports: [
		TopBarComponent,
		MatExpansionModule,
		MatIconModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatDatepickerModule,
		MatSlideToggle,
		MatCardModule,
		NgxMatTimepickerModule,
		TransportTimetableTableComponent,
		NgStyle,
		KeyValuePipe,
		NgClass,
		CustomMatLoaderComponent,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		ReactiveFormsModule,
		MatButtonModule,
	],
	templateUrl: './transport-timetable.component.html',
	styleUrl: './transport-timetable.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		TransportTimetableStore,
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				subscriptSizing: 'dynamic',
			},
		},
	],
})
export class TransportTimetableComponent implements OnInit, AfterViewInit {
	// enums
	readonly TransportAction = TransportAction;
	readonly TransportModule = TransportModule;
	readonly TransportSide = TransportSide;
	readonly TransportVehicleKind = TransportVehicleKind;
	readonly OfferTransportKind = OfferTransportKind;

	readonly store = inject(TransportTimetableStore);
	readonly renderer = inject(Renderer2);

	readonly transportKind = toSignal(
		inject(ActivatedRoute).queryParams.pipe(map((params) => params?.transportKind ?? OfferTransportKind.COACH)),
	);

	readonly stopLegend = {
		boarding: {
			name: 'Wsiadkowy',
			color: StopKindColor.BOARDING,
		},
		dropOff: {
			name: 'Wysiadkowy',
			color: StopKindColor.DROP_OFF,
		},
		cruising: {
			name: 'Przejazd przez',
			color: StopKindColor.CRUISING,
		},
		advanced: {
			name: 'Zaawansowane',
			color: StopKindColor.ADVANCED,
		},
		target: {
			name: 'Docelowy',
			color: StopKindColor.TARGET,
		},
	};

	timetables: Signal<TimetableWithNestedAntennas[]> = this.store.timetablesWithNestedAntennas;
	matCardTitleTopPosition = '0px';
	isExpandedPanelsArr: ExpansionPanelOpenState[] = [];

	readonly filteredOptionsWithoutSelectedVehicles = computed(() => {
		const selectedIds = new Set(this.store.groupedVehicles().map((g) => g.vehicle.id));
		return this.filteredOptions().filter((opt) => !selectedIds.has(opt.id));
	});

	private readonly filteredOptions = signal<TransportLogic['vehicle'][]>([]);

	private readonly stopsIds = computed(() => {
		return this.store
			.groupedVehicles()
			.flatMap((item) => {
				return item.timetable.map((v, index) => {
					if (!item.timetable[index + 1]) {
						return;
					}
					return { stopFrom: v.stop.id, stopTo: item.timetable[index + 1].stop.id };
				});
			})
			.filter(Boolean);
	});

	private readonly actionsAfterIsLoaded = rxMethod<boolean>(
		pipe(
			filter((element) => !element),
			tap(() => {
				this.store.fetchStopsConnections(this.stopsIds());
				this.isExpandedPanelsArr = this.mapToExpansionState(this.timetables(), true);
			}),
		),
	);

	private readonly dialog = inject(MatDialog);
	private readonly ngZone = inject(NgZone);

	ngOnInit(): void {
		this.actionsAfterIsLoaded(this.store.timetableIsLoading);
	}

	ngAfterViewInit(): void {
		const topBarEl = this.renderer.selectRootElement('app-top-bar', true);

		this.matCardTitleTopPosition = topBarEl ? `${topBarEl.offsetHeight}px` : `0px`;
	}

	changeToggleAllState(): void {
		const valueToSet = this.areAllRolledUp(this.isExpandedPanelsArr);
		this.isExpandedPanelsArr = this.mapToExpansionState(this.timetables(), valueToSet);
	}

	changeExpandedState(expansionPanel: ExpansionPanelOpenState, valueToSet: boolean): void {
		expansionPanel.expanded = valueToSet;

		const setExpandedToAntennas = (state: ExpansionPanelOpenState[]) =>
			state.forEach((antenna) => {
				antenna.expanded = valueToSet;
				setExpandedToAntennas(antenna.antennas);
			});

		setExpandedToAntennas(expansionPanel.antennas);
	}

	async openLuggageDialog(): Promise<void> {
		const transportLuggageComponent = await import('./transport-luggage/transport-luggage.component').then(
			(c) => c.TransportLuggageComponent,
		);

		this.ngZone.run(() => {
			this.dialog.open<
				TransportLuggageComponent,
				{ boardingTimetables: TransportTimetable[]; transportId: number }
			>(transportLuggageComponent, {
				data: { boardingTimetables: this.store.boardingTimetables(), transportId: this.store.transport()?.id },
				disableClose: true,
			});
		});
	}

	updateTimeDeparture(timeDeparture: string, timetableId: number, vehicleId: number): void {
		this.store.updateTimeDeparture(timeDeparture, timetableId, vehicleId);
	}

	updateStopConnection(stopConnection: StopConnection): void {
		this.store.updateStopConnection(stopConnection);
	}

	updateStopSale(stopSale: boolean, timetableId: number, vehicleId: number): void {
		this.store.updateStopSale(stopSale, timetableId, vehicleId);
	}

	updateFee(fee: number, timetableId: number, vehicleId: number): void {
		this.store.updateFee(fee, timetableId, vehicleId);
	}
	updateFlightNumber(flightNumber: string, timetableId: number, vehicleId: number): void {
		this.store.updateFlightNumber(flightNumber, timetableId, vehicleId);
	}

	updatePauseDuration(pauseDuration: number, timetableId: number, vehicleId: number): void {
		this.store.updatePauseDuration(pauseDuration, timetableId, vehicleId);

		const timeDeparture = this.store
			.groupedVehicles()
			.find((t) => t.vehicle.id === vehicleId)
			?.timetable.find((t) => t.id === timetableId)?.timeDeparture;

		if (!timeDeparture || !this.store.calculateTimeDepartures()) {
			return;
		}

		this.updateTimeDeparture(timeDeparture, timetableId, vehicleId);
	}

	updateAbroadAirportStop(abroadAirportStop: TimetableCity | null, timetableId: number, vehicleId: number): void {
		this.store.updateAbroadAirportStop(abroadAirportStop, timetableId, vehicleId);
	}

	updateAbroadAirportTime(abroadAirportTime: string | null, timetableId: number, vehicleId: number): void {
		this.store.updateAbroadAirportTime(abroadAirportTime, timetableId, vehicleId);
	}

	saveTimetable(): void {
		const earliestTimetables: TransportTimetable[] = Array.from(
			removeDuplicatesFromArray(
				this.store.groupedVehicles().flatMap((groupedVehicles) => {
					return groupedVehicles.timetable;
				}),
			)
				/*
 				This is a temporary solution until the backend supports
 				the ability to save several of the same cities in a timetable
				*/
				// TODO: Remove that reduce when backend has been ready
				.reduce((map, currentObj) => {
					const currentDate = new Date(currentObj.timeDeparture);

					if (
						!map.has(currentObj.stop.id) ||
						currentDate < new Date(map.get(currentObj.stop.id).timeDeparture)
					) {
						map.set(currentObj.stop.id, currentObj);
					}

					return map;
				}, new Map())
				.values(),
		).map((entry) => ({
			...entry,
			stop: entry.stop.id,
			abroadAirportStop: entry.abroadAirportStop?.id ?? null,
			abroadAirportTime: entry.abroadAirportTime ?? null,
		}));

		this.store.updateTimetables(earliestTimetables);
	}

	displayFn(user: TransportLogic['vehicle']): string {
		return user && user.name ? user.name : '';
	}

	filterVehiclesByName(value: string | TransportLogic['vehicle']): void {
		const name = typeof value === 'string' ? value : value?.name;
		this.filteredOptions.set(name ? this._filter(name as string) : this.store.transportVehicles().slice());
	}

	changeSelectedVehicle(currentVehicle: VehicleInGroupedTimetable, vehicleIdToUpdate: number): void {
		this.store.updateSelectedVehicle(currentVehicle, vehicleIdToUpdate);
		setTimeout(() => this.saveTimetable(), 200);
	}

	private _filter(name: string): TransportLogic['vehicle'][] {
		const filterValue = name.toLowerCase();

		return this.store.transportVehicles().filter((option) => option.name.toLowerCase().includes(filterValue));
	}

	private mapToExpansionState(
		timetables: TimetableWithNestedAntennas[],
		valueToSet: boolean,
	): ExpansionPanelOpenState[] {
		return timetables.map((timetable) => ({
			expanded: valueToSet,
			antennas: this.mapToExpansionState(timetable.antennasForVehicle, valueToSet),
		}));
	}

	private areAllRolledUp(states: ExpansionPanelOpenState[]): boolean {
		return states.every((state) => state.expanded === false && this.areAllRolledUp(state.antennas));
	}
}
